import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trash2, ExternalLink, Camera, X, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_LOGO_BYTES = 5 * 1024 * 1024;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const AdminSponsorsPage = () => {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [displayOrder, setDisplayOrder] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoData, setLogoData] = useState(null);

  const authConfig = useMemo(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('pcc_auth_token') : null;
    return token ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true } : { withCredentials: true };
  }, []);

  const fetchSponsors = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/sponsors`, authConfig);
      setSponsors(res.data || []);
    } catch (err) {
      console.error('Failed to load sponsors', err);
      toast.error('Failed to load sponsors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSponsors(); /* eslint-disable-next-line */ }, []);

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Logo must be 5MB or smaller');
      e.target.value = '';
      return;
    }
    try {
      const dataUrl = await fileToBase64(file);
      setLogoPreview(dataUrl);
      setLogoData({ base64: dataUrl, filename: file.name, content_type: file.type || 'image/png' });
    } catch (err) {
      console.error('Logo read failed', err);
      toast.error('Could not read logo file');
    }
  };

  const resetForm = () => {
    setName('');
    setUrl('');
    setDisplayOrder('');
    setLogoPreview(null);
    setLogoData(null);
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Sponsor name is required'); return; }
    if (!logoData) { toast.error('Please upload a logo'); return; }
    try {
      setSubmitting(true);
      const payload = {
        name: name.trim(),
        url: url.trim() || null,
        display_order: displayOrder === '' ? 0 : Number(displayOrder),
        logo_file_data: logoData.base64,
        logo_filename: logoData.filename,
        logo_content_type: logoData.content_type,
      };
      await axios.post(`${API_URL}/api/admin/sponsors`, payload, authConfig);
      toast.success('Sponsor added');
      resetForm();
      await fetchSponsors();
    } catch (err) {
      console.error('Create failed', err);
      const detail = err?.response?.data?.detail || 'Failed to add sponsor';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (sponsor_id, sponsorName) => {
    if (!window.confirm(`Remove "${sponsorName}" from the sponsor ticker?`)) return;
    try {
      setDeletingId(sponsor_id);
      await axios.delete(`${API_URL}/api/admin/sponsors/${sponsor_id}`, authConfig);
      toast.success('Sponsor removed');
      await fetchSponsors();
    } catch (err) {
      console.error('Delete failed', err);
      toast.error('Failed to remove sponsor');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-sponsors-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-to-dashboard-btn"
            aria-label="Back to admin dashboard"
          >
            <ArrowLeft className="w-5 h-5 text-navy" />
          </button>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter">Sponsors</h1>
            <p className="text-sm text-gray-500">Manage logos shown in the guest-page sponsor ticker</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
        {/* ADD SPONSOR FORM */}
        <section className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="add-sponsor-form">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
            <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight">Add Sponsor</h2>
            <p className="text-xs text-gray-500 mt-0.5">Logo, name, and an optional link.</p>
          </div>

          <div className="p-5 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sponsor-name" className="text-xs font-bold uppercase tracking-wider text-gray-500">Name <span className="text-red">*</span></label>
                <input
                  id="sponsor-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  placeholder="e.g. Park City Auto Group"
                  className="mt-2 w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
                  data-testid="sponsor-name-input"
                />
              </div>
              <div>
                <label htmlFor="sponsor-url" className="text-xs font-bold uppercase tracking-wider text-gray-500">Website <span className="text-gray-400">(optional)</span></label>
                <input
                  id="sponsor-url"
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="mt-2 w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
                  data-testid="sponsor-url-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sponsor-order" className="text-xs font-bold uppercase tracking-wider text-gray-500">Display order <span className="text-gray-400">(optional)</span></label>
                <input
                  id="sponsor-order"
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(e.target.value)}
                  placeholder="0"
                  className="mt-2 w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
                  data-testid="sponsor-order-input"
                />
                <p className="text-xs text-gray-400 mt-1">Lower numbers appear first. Defaults to 0.</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Logo <span className="text-red">*</span></label>
                <div className="mt-2 flex items-center gap-3">
                  {logoPreview ? (
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-1">
                      <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                      <button
                        type="button"
                        onClick={() => { setLogoPreview(null); setLogoData(null); }}
                        className="absolute top-1 right-1 p-1 bg-white/90 rounded-full hover:bg-white"
                        aria-label="Remove logo"
                        data-testid="remove-logo-btn"
                      >
                        <X className="w-3.5 h-3.5 text-navy" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-300">
                      <Camera className="w-6 h-6" />
                    </div>
                  )}
                  <label className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-navy hover:bg-gray-50 cursor-pointer" data-testid="upload-logo-trigger">
                    <Camera className="w-4 h-4" />
                    {logoPreview ? 'Replace logo' : 'Upload logo'}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoChange} data-testid="logo-upload-input" />
                  </label>
                </div>
                <p className="text-xs text-gray-400 mt-1">PNG/JPG/WebP/SVG, max 5MB. Transparent PNG recommended.</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
              <Button type="button" variant="ghost" onClick={resetForm} disabled={submitting} data-testid="reset-sponsor-form-btn">Reset</Button>
              <Button
                type="button"
                onClick={handleCreate}
                disabled={submitting || !name.trim() || !logoData}
                className="bg-navy hover:bg-navy/90 text-white"
                data-testid="add-sponsor-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {submitting ? 'Adding…' : 'Add Sponsor'}
              </Button>
            </div>
          </div>
        </section>

        {/* SPONSORS LIST */}
        <section className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="sponsors-list">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
            <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight">Current Sponsors</h2>
            <p className="text-xs text-gray-500 mt-0.5">Order shown matches the guest-page ticker.</p>
          </div>

          {loading ? (
            <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
          ) : sponsors.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-sm font-semibold text-gray-400">No sponsors yet</p>
              <p className="text-xs text-gray-400 mt-1">Add your first sponsor above.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {sponsors.map(s => (
                <li key={s.sponsor_id} className="flex items-center gap-4 p-4" data-testid={`sponsor-row-${s.sponsor_id}`}>
                  <div className="w-20 h-12 flex-shrink-0 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center p-1.5">
                    {s.logo_url ? (
                      <img src={s.logo_url} alt={s.name} className="max-w-full max-h-full object-contain" />
                    ) : (
                      <Camera className="w-5 h-5 text-gray-300" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-navy text-sm truncate">{s.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-500">Order: {s.display_order ?? 0}</span>
                      {s.url && (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-light-blue hover:underline"
                          data-testid={`sponsor-link-${s.sponsor_id}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          {s.url.replace(/^https?:\/\//, '').slice(0, 32)}
                        </a>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(s.sponsor_id, s.name)}
                    disabled={deletingId === s.sponsor_id}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-red hover:underline disabled:opacity-50"
                    data-testid={`delete-sponsor-${s.sponsor_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {deletingId === s.sponsor_id ? 'Removing…' : 'Remove'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminSponsorsPage;
